(() => {
  if (customElements.get('volt-navigation')) return;
  const countCache = new Map();
  const countQueue = [];
  let activeCountRequests = 0;
  const drainCounts = () => {
    while (activeCountRequests < 4 && countQueue.length) {
      const job = countQueue.shift();
      activeCountRequests++;
      job.run().then(job.resolve, job.reject).finally(() => { activeCountRequests--; drainCounts(); });
    }
  };
  const loadCount = href => {
    if (!countCache.has(href)) {
      countCache.set(href, new Promise((resolve, reject) => {
        countQueue.push({ resolve, reject, run: async () => {
          const url = new URL(href, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid count destination');
          url.searchParams.set('section_id', 'volt-navigation-count');
          const response = await fetch(url.pathname + url.search, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Count unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = document.querySelector('[data-vnm-count-json]');
          if (!payload) throw new Error('Missing count');
          const data = JSON.parse(payload.textContent);
          if (!Number.isInteger(data.count) || data.count < 0) throw new Error('Invalid count');
          return data;
        }});
        drainCounts();
      }));
    }
    return countCache.get(href);
  };
  customElements.define('volt-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const payload = this.querySelector('[data-vn-data]');
      const tree = this.querySelector('[data-vn-tree]');
      if (!payload || !tree) return;
      let nodes;
      try { nodes = JSON.parse(payload.textContent); } catch (_) { this.querySelector('[data-vn-empty]').hidden = false; return; }
      this.dataset.ready = 'true';
      const includeHidden = this.dataset.includeHidden === 'true';
      const byId = new Map(nodes.map(node => [node.id, node]));
      const children = new Map();
      nodes.forEach(node => {
        if (!includeHidden && node.visible !== true) return;
        const parent = node.parent || null;
        if (!children.has(parent)) children.set(parent, []);
        children.get(parent).push(node);
      });
      children.forEach(list => list.sort((a, b) => Number(a.order) - Number(b.order) || String(a.name).localeCompare(String(b.name))));
      const destination = node => {
        if (!node.url) return null;
        const url = new URL(node.url, location.origin);
        return url.origin === location.origin && url.pathname.includes('/collections/') ? url.pathname + url.search : null;
      };
      const labels = JSON.parse(this.querySelector('[data-vn-labels]').textContent);
      const id = this.dataset.sectionId;
      const label = (tag, text, cls) => { const el = document.createElement(tag); el.textContent = text || ''; if (cls) el.className = cls; return el; };
      const addCount = (element, node) => {
        if (this.dataset.showCounts === 'false') return;
        const href = destination(node);
        if (!href) return;
        const badge = label('span', '', 'vnm-n');
        if (Number.isInteger(node.count) && node.count >= 0) badge.textContent = String(node.count);
        else { badge.hidden = true; badge.dataset.vnmCountUrl = href; }
        element.append(badge);
      };
      const hydrateCounts = scope => {
        scope.querySelectorAll('[data-vnm-count-url]').forEach(badge => {
          if (badge.dataset.loading) return;
          badge.dataset.loading = 'true';
          loadCount(badge.dataset.vnmCountUrl).then(data => {
            if (!badge.isConnected) return;
            badge.textContent = String(data.count) + (data.capped ? '+' : '');
            badge.hidden = false;
          }).catch(() => {});
        });
      };
      const link = (node, cls, title = node.name) => {
        const href = destination(node);
        const el = label(href ? 'a' : 'span', title, cls);
        if (href) el.href = href;
        if (!['vnm-link', 'vnm-all', 'vnm-desktop-all'].some(excluded => (cls || '').split(' ').includes(excluded))) addCount(el, node);
        return el;
      };
      const button = (text, cls, action) => {
        const el = label('button', text, cls); el.type = 'button'; el.setAttribute(action, ''); return el;
      };
      const listFor = (parent, visited = new Set(), depth = 0) => {
        const list = document.createElement('ul'); list.className = 'vnm-list';
        (children.get(parent) || []).forEach(node => {
          if (visited.has(node.id) || depth > 20) return;
          const path = new Set(visited); path.add(node.id);
          const item = document.createElement('li');
          if ((children.get(node.id) || []).length && node.expandInMenu !== false) {
            const nested = document.createElement('details'); nested.className = 'vnm-nested';
            const summary = label('summary', node.name); addCount(summary, node);
            nested.append(summary, link(node, 'vnm-all', labels.viewAll));
            nested.append(listFor(node.id, path, depth + 1)); item.append(nested);
          } else item.append(link(node));
          list.append(item);
        });
        const extraItems = [...list.children].slice(6);
        if (extraItems.length) {
          extraItems.forEach(item => { item.hidden = true; item.dataset.vnmOverflow = ''; });
          const controlItem = document.createElement('li');
          const more = button('Show more', 'vnm-show-more', 'data-vnm-show-more');
          more.setAttribute('aria-expanded', 'false');
          more.addEventListener('click', () => {
            const expanded = more.getAttribute('aria-expanded') !== 'true';
            extraItems.forEach(item => { item.hidden = !expanded; });
            more.setAttribute('aria-expanded', String(expanded));
            more.textContent = expanded ? 'Show less' : 'Show more';
          });
          controlItem.append(more); list.append(controlItem);
        }
        return list;
      };
      const root = document.createElement('div'); root.className = 'vnm'; root.setAttribute('data-vnm','');
      root.addEventListener('vnm:open', event => hydrateCounts(event.detail.panel));
      const scrim = document.createElement('div'); scrim.className = 'vnm-scrim'; scrim.setAttribute('data-vnm-scrim','');
      const shell = document.createElement('div'); shell.className = 'vnm-shell'; shell.id = 'VoltNavigationMenu-' + id; shell.tabIndex = -1; shell.setAttribute('aria-label',this.dataset.heading);
      const head = document.createElement('div'); head.className = 'vnm-mhead';
      const close = button('×','vnm-close','data-vnm-close-drawer'); close.setAttribute('aria-label',labels.close);
      head.append(close,label('span',labels.drawerTitle || this.dataset.heading,'vnm-drawer-title')); shell.append(head);
      const bar = document.createElement('nav'); bar.className = 'vnm-bar'; bar.setAttribute('aria-label',this.dataset.heading);
      const row = document.createElement('ul'); row.className = 'vnm-row';
      (children.get(null) || []).forEach((node,index) => {
        const item = document.createElement('li'); item.className = 'vnm-item'; item.setAttribute('data-vnm-item','');
        const groups = children.get(node.id) || [];
        const topLink = link(node, 'vnm-link');
        const text = label('span', '', 'vnm-link__txt');
        text.append(label('span', node.name, 'vnm-link__lab'), label('span', groups.length ? labels.categories.replace('[count]', String(groups.length)) : labels.goTo.replace('[title]', node.name), 'vnm-link__sub'));
        topLink.replaceChildren(text);
        if (node.thumbnail) {
          const image = document.createElement('img');
          image.className = 'vnm-thumb'; image.src = node.thumbnail; image.alt = ''; image.width = 42; image.height = 42; image.loading = 'lazy';
          topLink.prepend(image);
        }
        if (!groups.length) { item.append(topLink); row.append(item); return; }
        const entry = document.createElement('div'); entry.className = 'vnm-entry';
        const toggle = button('','vnm-toggle','data-vnm-toggle');
        toggle.setAttribute('aria-label',labels.open.replace('[title]',node.name)); toggle.setAttribute('aria-expanded','false');
        const panelId = 'VoltNavigationPanel-' + id + '-' + index; toggle.setAttribute('aria-controls', panelId);
        const caret = document.createElementNS('http://www.w3.org/2000/svg','svg');
        caret.setAttribute('class','vnm-caret'); caret.setAttribute('width','10'); caret.setAttribute('height','7'); caret.setAttribute('viewBox','0 0 10 7'); caret.setAttribute('fill','none'); caret.setAttribute('aria-hidden','true');
        const stroke = document.createElementNS(caret.namespaceURI,'path'); stroke.setAttribute('d','M1 1.5 5 5.5 9 1.5'); stroke.setAttribute('stroke','currentColor'); stroke.setAttribute('stroke-width','1.7'); stroke.setAttribute('stroke-linecap','round'); caret.append(stroke); toggle.append(caret);
        entry.append(topLink,toggle); item.append(entry);
        const panel = document.createElement('div'); panel.className = 'vnm-panel'; panel.id = panelId; panel.setAttribute('data-vnm-panel',''); panel.inert = true;
        const panelHead = document.createElement('div'); panelHead.className = 'vnm-panel__head';
        panelHead.append(button('‹ ' + labels.back,'vnm-back','data-vnm-back'),label('span',node.name,'vnm-panel__title'),link(node,'vnm-all',labels.viewAll + ' ›'));
        panel.append(panelHead);
        const cols = document.createElement('div'); cols.className = 'vnm-cols'; cols.style.setProperty('--cols',Math.min(groups.length,6));
        cols.append(link(node,'vnm-desktop-all',labels.all.replace('[title]',node.name) + ' ›'));
        groups.forEach((group,groupIndex) => {
          const container = document.createElement('div'); container.className = 'vnm-group';
          const descendants = children.get(group.id) || [];
          if (descendants.length) {
            container.classList.add('vnm-group--acc'); container.dataset.open = 'false';
            container.append(link(group,'vnm-group__t vnm-group__desktop'));
            const accordion = button(group.name,'vnm-group__t vnm-group__mobile','data-vnm-acc'); addCount(accordion, group); accordion.setAttribute('aria-expanded','false');
            const list = listFor(group.id); list.id = panelId + '-group-' + groupIndex; accordion.setAttribute('aria-controls',list.id);
            container.append(accordion,list,link(group,'vnm-all vnm-group-all',labels.all.replace('[title]',group.name) + ' ›'));
          } else container.append(link(group,'vnm-group__t'));
          cols.append(container);
        });
        panel.append(cols);
        const discoveryUrl = node.discoveryUrl || node.url;
        if (discoveryUrl && discoveryUrl.includes('/collections/')) {
          const discovery = document.createElement('div'); discovery.className = 'vn-discovery-row';
          const quick = document.createElement('div'); quick.className = 'vn-discovery-quick';
          quick.append(label('h3', 'Explore ' + node.name, 'vmm-discovery__heading'));
          [['New arrivals','created-descending'],['Best sellers','best-selling']].forEach(([title,sort]) => {
            const a = document.createElement('a'); const url = new URL(discoveryUrl,location.origin);
            url.searchParams.set('sort_by',sort); a.href = url.pathname + url.search; a.textContent = title + ' ›'; a.dataset.shortcut = sort; quick.append(a);
          });
          discovery.append(quick);
          const discoveryModes = node.name.trim().toLowerCase() === 'modular' ? ['mega_filters','mega_brands'] : ['mega_brands'];
          discoveryModes.forEach(mode => {
            const template = this.querySelector('[data-vn-discovery-template]');
            const block = template.content.firstElementChild.cloneNode(true);
            block.className = 'vmm-discovery vn-discovery-block vn-discovery-' + mode;
            Object.assign(block.dataset,{source:discoveryUrl,mode,limit:'4',functions:'6',hp:'true',diy:'false',showFunctions:'true'});
            const labelsElement = block.querySelector('[data-discovery-labels]');
            const blockLabels = JSON.parse(labelsElement.textContent); blockLabels.brands = 'Brands';
            labelsElement.textContent = JSON.stringify(blockLabels);
            const fallback = block.querySelector('[data-discovery-fallback]'); fallback.textContent = 'Loading…';
            discovery.append(block);
          });
          panel.append(discovery);
        }
        item.append(panel); row.append(item);
      });
      const hotTemplate = this.querySelector('[data-vn-hot-template]');
      if (hotTemplate?.content.querySelector('a')) row.append(hotTemplate.content.cloneNode(true));
      bar.append(row); shell.append(bar);
      const footerTemplate = this.querySelector('[data-vn-mobile-footer]');
      if (footerTemplate) {
        const footer = footerTemplate.content.cloneNode(true);
        const language = footer.querySelector('.vnm-localization');
        if (language?.querySelector('select[name="language_code"]')) head.append(language);
        else language?.remove();
        shell.append(footer);
      } root.append(scrim,shell); tree.replaceChildren(root);
      this.querySelector('[data-vn-empty]').hidden = row.childElementCount > 0;
      this.attachToHeader();
      window.voltNavigationMenuBoot?.();
    }
    attachToHeader() {
      const navigation = document.querySelector('.header-section .header__bottom');
      if (!navigation || navigation.contains(this)) return;
      this.navigationSection ||= this.closest('.shopify-section');
      if (!this.navigationSection) return;
      // Share the existing sticky/collapsible header rather than a second scroll controller.
      navigation.append(this);
    }
    restoreSection() {
      if (this.navigationSection?.isConnected && !this.navigationSection.contains(this)) {
        this.navigationSection.append(this);
      }
    }
  });
})();

// Same interaction controller as the current menu, scoped to the new section.

(() => {
  if (window.voltNavigationMenuBoot) { window.voltNavigationMenuBoot(); return; }
  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const controller = new AbortController();
    const on = (target, name, callback) => target.addEventListener(name, callback, {signal: controller.signal});
    const desktop = matchMedia('(min-width: 1024px)');
    const items = [...root.querySelectorAll('[data-vnm-item]')];
    const shell = root.querySelector('.vnm-shell');
    const header = root.closest('volt-navigation');
    const legacyTriggers = [...document.querySelectorAll('[data-vmm-open-drawer]')];
    const triggers = [...header.querySelectorAll('[data-vnm-open-drawer]'), ...legacyTriggers];
    if (legacyTriggers.length) header.classList.add('vn-uses-header-trigger');
    const previousTriggerAttributes = legacyTriggers.map(button => [button, button.getAttribute('aria-controls'), button.getAttribute('aria-expanded')]);
    legacyTriggers.forEach(button => { button.setAttribute('aria-controls', shell.id); button.setAttribute('aria-expanded', 'false'); });
    let active = null, opener = null, openTimer, closeTimer, previousOverflow;
    let inertElements = [];
    const visibleFocusable = scope => [...scope.querySelectorAll('a[href],button:not([disabled]),select,input:not([type="hidden"]),[tabindex="0"]')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[inert]'));
    const clearTimers = () => { clearTimeout(openTimer); clearTimeout(closeTimer); };
    function closePanel(restoreFocus = false) {
      clearTimers();
      const previous = active;
      active = null;
      items.forEach(item => {
        item.classList.remove('is-open');
        item.querySelector('[data-vnm-toggle]')?.setAttribute('aria-expanded', 'false');
        item.querySelector('[data-vnm-panel]')?.setAttribute('inert', '');
      });
      root.classList.remove('is-open');
      if (restoreFocus) previous?.querySelector('[data-vnm-toggle]')?.focus();
    }
    function openPanel(item, focus = false) {
      clearTimers();
      closePanel();
      active = item;
      item.classList.add('is-open');
      item.querySelector('[data-vnm-toggle]').setAttribute('aria-expanded', 'true');
      const panel = item.querySelector('[data-vnm-panel]');
      panel.removeAttribute('inert');
      root.classList.add('is-open');
      root.dispatchEvent(new CustomEvent('vnm:open', { detail: { panel } }));
      if (focus || !desktop.matches) visibleFocusable(panel)[0]?.focus();
    }
    function setDrawer(open, restoreFocus = true) {
      closePanel();
      if (open === root.classList.contains('is-drawer')) return;
      root.classList.toggle('is-drawer', open);
      triggers.forEach(button => button.setAttribute('aria-expanded', String(open)));
      if (open) {
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        shell.inert = false;
        shell.setAttribute('role', 'dialog');
        shell.setAttribute('aria-modal', 'true');
        let ancestor = shell;
        while (ancestor.parentElement && ancestor !== document.body) {
          [...ancestor.parentElement.children].filter(el => el !== ancestor && !['SCRIPT','STYLE','LINK'].includes(el.tagName)).forEach(el => {
            inertElements.push([el, el.inert]); el.inert = true;
          });
          ancestor = ancestor.parentElement;
        }
        root.querySelector('[data-vnm-close-drawer]').focus();
      } else {
        document.documentElement.style.overflow = previousOverflow ?? '';
        inertElements.forEach(([el, value]) => { el.inert = value; });
        inertElements = [];
        shell.removeAttribute('role');
        shell.removeAttribute('aria-modal');
        shell.inert = !desktop.matches;
        if (restoreFocus) opener?.focus();
      }
    }
    items.forEach(item => {
      const button = item.querySelector('[data-vnm-toggle]');
      on(item, 'mouseenter', () => {
        if (!desktop.matches || !matchMedia('(hover:hover)').matches) return;
        clearTimers();
        if (!button) { closePanel(); return; }
        openTimer = setTimeout(() => openPanel(item), active ? 0 : 110);
      });
      if (!button) return;
      on(button, 'click', () => active === item ? closePanel() : openPanel(item));
      [button, item.querySelector('.vnm-link')].forEach(control => on(control, 'keydown', event => {
        if (desktop.matches && event.key === 'ArrowDown') {
          event.preventDefault(); openPanel(item, true);
        }
      }));
    });
    on(root, 'mouseleave', () => {
      if (!desktop.matches) return;
      clearTimeout(openTimer); closeTimer = setTimeout(() => closePanel(), 220);
    });
    on(root, 'mouseenter', () => clearTimeout(closeTimer));
    on(root, 'change', event => {
      if (event.target.matches('.vnm-localization select')) event.target.form.requestSubmit();
    });
    on(root, 'click', event => {
      if (event.target.closest('[data-vnm-back]')) closePanel(true);
      if (event.target.closest('[data-vnm-close-drawer]')) setDrawer(false);
      if (event.target.closest('[data-vnm-scrim]')) closePanel();
      const accordion = event.target.closest('[data-vnm-acc]');
      if (accordion && !desktop.matches) {
        const group = accordion.closest('.vnm-group');
        const open = group.dataset.open !== 'true';
        group.dataset.open = String(open);
        accordion.setAttribute('aria-expanded', String(open));
      }
    });
    triggers.forEach(button => button.addEventListener('click', event => {
      if (desktop.matches) return;
      event.preventDefault(); event.stopImmediatePropagation();
      opener = button; setDrawer(!root.classList.contains('is-drawer'));
    }, {capture: true, signal: controller.signal}));
    on(document, 'keydown', event => {
      if (event.key === 'Escape') {
        if (active) { event.preventDefault(); closePanel(true); }
        else if (root.classList.contains('is-drawer')) { event.preventDefault(); setDrawer(false); }
      }
      if (event.key === 'Tab' && root.classList.contains('is-drawer')) {
        const scope = active ? active.querySelector('[data-vnm-panel]') : shell;
        const elements = visibleFocusable(scope), first = elements[0], last = elements.at(-1);
        if (event.shiftKey && (document.activeElement === first || !scope.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !scope.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    });
    on(root, 'keydown', event => {
      if (!desktop.matches || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      const buttons = items.map(item => item.querySelector('.vnm-link'));
      const index = items.indexOf(document.activeElement.closest('[data-vnm-item]'));
      if (!document.activeElement.matches('.vnm-link, [data-vnm-toggle]')) return;
      if (index < 0) return;
      event.preventDefault(); closePanel();
      buttons[(index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length].focus();
    });
    on(root, 'focusout', event => { if (desktop.matches && !root.contains(event.relatedTarget)) closePanel(); });
    on(document, 'pointerdown', event => { if (desktop.matches && !root.contains(event.target)) closePanel(); });
    on(desktop, 'change', () => {
      setDrawer(false, false); closePanel(); shell.inert = !desktop.matches;
    });
    on(document, 'shopify:block:select', event => {
      if (!root.contains(event.target)) return;
      const item = event.target.closest('[data-vnm-item]');
      if (item) { if (!desktop.matches) setDrawer(true); openPanel(item); }
    });
    on(document, 'shopify:section:unload', event => {
      if (!event.target.contains(root) && event.target !== header.navigationSection) return;
      setDrawer(false, false); clearTimers(); controller.abort();
      delete root.dataset.ready;
      header.restoreSection();
      previousTriggerAttributes.forEach(([button, controls, expanded]) => {
        if (controls === null) button.removeAttribute('aria-controls'); else button.setAttribute('aria-controls', controls);
        if (expanded === null) button.removeAttribute('aria-expanded'); else button.setAttribute('aria-expanded', expanded);
      });
    });
    closePanel(); shell.inert = !desktop.matches;
  }
  window.voltNavigationMenuBoot = () => {
    document.querySelectorAll('volt-navigation').forEach(header => header.attachToHeader?.());
    document.querySelectorAll('[data-vnm]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', window.voltNavigationMenuBoot);
  else window.voltNavigationMenuBoot();
  document.addEventListener('shopify:section:load', window.voltNavigationMenuBoot);
})();

